import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "motion/react";
import { Button } from "../ui/button";
import { Sparkles, Star, Crown, Send } from "lucide-react";
// @ts-ignore
import { Navbar } from "../landing-page/Navbar";
// @ts-ignore
import { Footer } from "../landing-page/Footer";
import { api } from "../../services/api";

interface PricingPageProps {
  onSignIn?: () => void;
  onRegister?: () => void;
}

export function PricingPage({ onSignIn, onRegister }: PricingPageProps) {
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Release Timer Logic
  const RELEASE_DATE = "2026-06-29T09:00:00+07:00"; // Senin, 29 Juni 2026 09:00 WIB
  const [isReleased, setIsReleased] = useState(false);
  const [releaseTimeLeft, setReleaseTimeLeft] = useState<string>('');

  useEffect(() => {
    const checkRelease = () => {
      // Test mode bypass
      if ((import.meta as any).env.VITE_TEST_SUBSCRIPTION === 'true') {
        setIsReleased(true);
        return;
      }
      
      const now = new Date().getTime();
      const release = new Date(RELEASE_DATE).getTime();
      const difference = release - now;

      if (difference <= 0) {
        setIsReleased(true);
      } else {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        let timeString = '';
        if (days > 0) timeString += `${days}d `;
        timeString += `${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;
        setReleaseTimeLeft(timeString);
      }
    };

    checkRelease();
    const timer = setInterval(checkRelease, 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        const response = await api.getSubscriptionPlans();
        if (response.success && Array.isArray(response.data)) {
          setPlans(response.data);
        }
      } catch (error) {
        console.error('Failed to load subscription plans:', error);
      } finally {
        setLoading(false);
      }
    };
    loadPlans();
  }, []);

  const handleGetStarted = () => {
    if (typeof onRegister === "function") {
      onRegister();
    } else {
      window.location.href = "/auth?mode=register";
    }
  };

  return (
    <div className="relative min-h-screen lg:h-[100dvh] flex flex-col bg-gradient-to-br from-background via-muted/20 to-background lg:overflow-hidden">
      <Helmet>
        <title>Harga - BandarmoloNY</title>
        <meta
          name="description"
          content="Pilih paket yang sesuai dengan kebutuhan trading Anda. Harga terjangkau dengan fitur canggih."
        />
        <link rel="canonical" href="https://bandarmolony.com/pricing" />
        <meta
          property="og:title"
          content="Harga - BandarmoloNY"
        />
        <meta
          property="og:description"
          content="Pilih paket yang sesuai dengan kebutuhan trading Anda. Harga terjangkau dengan fitur canggih."
        />
        <meta property="og:url" content="https://bandarmolony.com/pricing" />
        <meta property="og:image" content="https://bandarmolony.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Harga - BandarmoloNY"
        />
        <meta
          name="twitter:description"
          content="Pilih paket yang sesuai dengan kebutuhan trading Anda. Harga terjangkau dengan fitur canggih."
        />
        <meta name="twitter:image" content="https://bandarmolony.com/og-image.jpg" />
      </Helmet>

      {/* Background decorative elements removed to make it plain */}

      <Navbar
        {...(onSignIn !== undefined ? { onSignIn } : {})}
        {...(onRegister !== undefined ? { onRegister } : {})}
      />

      <main className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col gap-3 px-4 pb-2 pt-20 md:px-8 md:pb-4 md:pt-24 overflow-y-auto">
        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center shrink-0"
        >
          <h1 className="mt-1 text-2xl font-bold tracking-tight md:text-3xl">
            Choose Your Plan
          </h1>
          <p className="mx-auto mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
            {isReleased 
              ? "All plans grant full access to all features. Choose what works best for you."
              : "Subscription plans will be available shortly. Stay tuned!"}
          </p>
          {!isReleased && (
            <div className="flex justify-center mt-3">
              <div className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 border-transparent bg-yellow-500/10 text-yellow-500">
                Available Monday, 29 June 2026 at 09:00 WIB
              </div>
            </div>
          )}
        </motion.header>

        {/* Pricing Cards */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          </div>
        ) : (
          <section className="container mx-auto mt-8 mb-10 grid max-w-7xl grid-cols-1 gap-4 px-2 md:grid-cols-2 lg:grid-cols-4">
            {plans.map((plan, index) => (
              <motion.article
                key={plan.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className={`relative flex h-full flex-col min-h-0 overflow-hidden rounded-3xl border border-white/10 bg-background/80 p-3 shadow-xl backdrop-blur transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_35px_rgba(239,68,68,0.4)] dark:border-white/5 ${plan.popular
                  ? 'ring-2 ring-primary'
                  : ''
                  }`}
              >
                {plan.popular && (
                  <div className="absolute right-[-40px] top-[20px] w-[150px] rotate-45 bg-yellow-400 py-1 text-center text-xs font-bold text-black shadow-sm z-10 tracking-wider">
                    BEST VALUE
                  </div>
                )}

                <div className="mb-2 flex items-center justify-center">
                  {plan.popular ? (
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Star className="w-4 h-4 text-primary" />
                    </div>
                  ) : index === 0 ? (
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-primary" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                      <Crown className="w-4 h-4 text-primary" />
                    </div>
                  )}
                </div>

                <h2 className="mb-1 text-center text-lg font-bold text-foreground">
                  {plan.name}
                </h2>


                <div className="mb-3 text-center">
                  <div className="flex items-baseline justify-center gap-1.5">
                    <div className="text-2xl font-bold text-foreground">
                      <span className="text-lg">Rp</span> <span>{Math.floor(Number(plan.price) / 1000).toLocaleString()}</span>
                      <span className="text-lg">,{String(Number(plan.price) % 1000).padStart(3, '0')}</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      /{plan.period}
                    </div>
                  </div>
                  
                  {(plan.id === '3_months' || plan.id === '6_months' || plan.id === '12_months') && (
                    <div className="mx-auto mt-2.5 mb-1.5 w-fit overflow-hidden rounded-sm shadow-md border border-[#408dbd]">
                      <div className="bg-gradient-to-b from-[#358bc2] to-[#125887] py-1.5 px-4 text-center relative border-t border-t-[#66ade0] border-l border-l-[#66ade0] shadow-inner">
                        {/* Folded corner effect (bottom right silver) */}
                        <div className="absolute bottom-0 right-0 w-0 h-0 border-b-[16px] border-l-[16px] border-b-[#e2e8f0] border-l-transparent drop-shadow-[-1px_-1px_1px_rgba(0,0,0,0.4)]"></div>
                        
                        <div className="text-[12px] font-black text-[#fde047] uppercase tracking-wide leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                          FREE {plan.id === '3_months' ? 1 : plan.id === '6_months' ? 2 : 3} MONTH{plan.id === '3_months' ? '' : 'S'}
                        </div>
                        <div className="text-[10px] font-bold text-white uppercase tracking-wider mb-1 leading-tight drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
                          LIVE SCREENER ALERT
                        </div>
                        <div className="flex items-center justify-center gap-1 text-[9px] font-medium text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.8)]">
                          <div className="bg-[#2AABEE] rounded-full p-0.5 inline-flex items-center justify-center">
                            <Send className="w-2.5 h-2.5 text-white -ml-0.5" />
                          </div>
                          Via Telegram
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex-1" />

                {!isReleased ? (
                  <Button
                    className="mt-auto w-full py-2 text-xs font-medium tracking-tight bg-slate-800 text-white hover:bg-slate-700 border border-slate-700 opacity-90"
                    onClick={() => {
                      // Optional: add toast if we have sonner, else just use alert
                      alert('Subscription feature is currently locked. Stay tuned for the launch on Monday, 29 June 2026 at 09:00 WIB!');
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 mr-1.5"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                    {releaseTimeLeft}
                  </Button>
                ) : (
                  <Button
                    className={`mt-auto w-full py-2 text-xs font-semibold transition-all duration-300 ${plan.popular
                      ? 'bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg'
                      : 'bg-background border-2 border-primary text-primary hover:bg-accent hover:text-white dark:hover:bg-accent dark:hover:text-white'
                      }`}
                    variant={plan.popular ? "default" : "outline"}
                    onClick={handleGetStarted}
                  >
                    {plan.popular ? "Subscribe" : "Subscribe"}
                  </Button>
                )}
              </motion.article>
            ))}
          </section>
        )}

        {/* CTA Section */}
        <motion.section
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="rounded-2xl border border-white/10 bg-background/60 p-3 text-center backdrop-blur dark:border-white/5 md:p-4 shrink-0"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="text-left md:max-w-xl">
              <h2 className="text-lg font-semibold md:text-xl">Upgrade the way you trade!</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Join BandarmoloNY today, start Free trial.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              <Button
                size="default"
                onClick={handleGetStarted}
                className="transform rounded-xl border-0 bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground shadow-lg transition-all duration-300 hover:-translate-y-1 hover:scale-105 hover:bg-primary/90"
              >
                Free Trial
              </Button>
              <Button
                size="default"
                variant="outline"
                onClick={onSignIn}
                className="transform rounded-xl px-4 py-2 text-sm font-semibold transition-all duration-300 hover:-translate-y-1 hover:scale-105"
              >
                Sign In
              </Button>
            </div>
          </div>
        </motion.section>
      </main>

      {/* SEO Backlinks Hidden Section */}
      <div className="sr-only">
        <h2>Subscription Plans by Hints Technology</h2>
        <nav>
          <ul>
            <li><a href="https://www.hintstechnology.com/" rel="nofollow noopener noreferrer">Hints Technology Services</a></li>
            <li><a href="https://bandarmolony.com/" rel="nofollow noopener noreferrer">BandarmoloNY Landing Page</a></li>
            <li><a href="https://bandarmolony.com/features" rel="nofollow noopener noreferrer">View All Features</a></li>
            <li><a href="https://bandarmolony.com/contact" rel="nofollow noopener noreferrer">Contact Support Team</a></li>
          </ul>
        </nav>
        <p>Affordable trading platform subscriptions developed by Hints Technology. Choose the right plan for your trading needs.</p>
      </div>

      <Footer />
    </div>
  );
}
